import tinycolor from 'tinycolor2';

const DEFAULT_HEX8 = '#ffffffff'; // opaque white — matches the previous Color fallbacks

/**
 * In-memory color value object for player entities (starters / pois / tracks).
 *
 * The canonical form is a normalized 8-digit hex string `#RRGGBBAA`; a 6-digit input is
 * treated as fully opaque (alpha 1.0) by appending `ff`. Every output format (`hex`,
 * `hexNum`, `rgba`, `alpha`, `isLight`, `toRgbaTuple`) is derived lazily from a single
 * cached tinycolor instance, so construction is cheap and reads are fast after first access.
 *
 * Instances live in the (read-only) player store and must be treated as immutable — they are
 * never mutated, frozen, cloned, or serialized. Persistence uses a plain hex string elsewhere.
 */
export class PlayerColor {
  /** Normalized canonical color, always `#RRGGBBAA`. */
  readonly hex8: string;
  #tc: tinycolor.Instance | undefined;

  private constructor(hex8: string) {
    this.hex8 = hex8;
  }

  private get tc(): tinycolor.Instance {
    if (this.#tc == null) this.#tc = tinycolor(this.hex8);
    return this.#tc;
  }

  /** Parse from a CSS color string (`#RGB`, `#RRGGBB`, `#RRGGBBAA`, `rgb()`, `rgba()`, names…). */
  static fromString(input: string | null | undefined): PlayerColor {
    const parsed = input == null ? null : tinycolor(input);
    return parsed?.isValid() ? new PlayerColor(toHex8(parsed)) : PlayerColor.default();
  }

  /** Parse from a `[r, g, b, a?]` tuple where r/g/b and a are 0-255 (backend track/poi array form). */
  static fromRgbaArray(arr: ReadonlyArray<number>): PlayerColor {
    const parsed = tinycolor({ r: arr[0], g: arr[1], b: arr[2], a: (arr[3] ?? 255) / 255 });
    return parsed.isValid() ? new PlayerColor(toHex8(parsed)) : PlayerColor.default();
  }

  /** Accepts either form, mirroring the previous `extractColor(color: string | number[])` signature. */
  static from(input: string | ReadonlyArray<number> | null | undefined): PlayerColor {
    if (input == null) return PlayerColor.default();
    // `typeof` narrows the union cleanly; `Array.isArray` would not strip `ReadonlyArray`.
    return typeof input === 'string'
      ? PlayerColor.fromString(input)
      : PlayerColor.fromRgbaArray(input);
  }

  /** Opaque white — the shared fallback for missing/invalid colors. */
  static default(): PlayerColor {
    return new PlayerColor(DEFAULT_HEX8);
  }

  /** 6-digit `#RRGGBB` (alpha dropped) — for CSS display and strokes. */
  get hex(): string {
    return this.tc.toHexString();
  }

  /**
   * RGB-only integer (NO alpha byte) — sprite-cache key and render tint source.
   * MUST stay RGB-only: it is unpacked via bit-shifts and used to share sprite textures
   * across alpha variants of the same color.
   */
  get hexNum(): number {
    return Number.parseInt(this.tc.toHex(), 16);
  }

  /** Luminance flag used to pick an auto-contrast stroke (black on light, white on dark). */
  get isLight(): boolean {
    return this.tc.isLight();
  }

  /**
   * `rgba(r,g,b,a)` / `rgb(r,g,b)` CSS string (encodes alpha).
   * MUST stay in rgb()/rgba() form: `validateColor` and the mapbox `line-color` depend on it.
   */
  get rgba(): string {
    return this.tc.toRgbString();
  }

  /** Alpha as a 0-1 float. A 6-digit (channel-less) source color yields 1. */
  get alpha(): number {
    return this.tc.getAlpha();
  }

  /** WebGL/deck.gl color tuple `[r, g, b, a*255]`, all 0-255. */
  toRgbaTuple(): [number, number, number, number] {
    const { r, g, b, a } = this.tc.toRgb();
    return [r, g, b, Math.round(a * 255)];
  }
}

// 8-digit hex built manually: tinycolor2@1.6.0 has no `toHex8String` (see colorUtils.ts).
function toHex8(c: tinycolor.Instance): string {
  const { r, g, b, a } = c.toRgb();
  const h = (n: number) => Math.round(n).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}${h(a * 255)}`;
}
